import React from 'react';
import {
  Box, Button, FormControl, InputLabel, MenuItem, Select,
  Typography, IconButton, Tooltip, TextField, Chip,
  OutlinedInput, Checkbox, ListItemText, Divider,
} from '@mui/material';
import SearchIcon          from '@mui/icons-material/Search';
import FilterAltIcon       from '@mui/icons-material/FilterAlt';
import ScheduleIcon        from '@mui/icons-material/Schedule';
import CategoryIcon        from '@mui/icons-material/Category';
import PriorityHighIcon    from '@mui/icons-material/PriorityHigh';
import EventAvailableIcon  from '@mui/icons-material/EventAvailable';
import HelpOutlineIcon     from '@mui/icons-material/HelpOutline';
import PlaceIcon           from '@mui/icons-material/Place';
import VideocamIcon        from '@mui/icons-material/Videocam';
import { AdapterDateFns }  from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DateTimePicker } from '@mui/x-date-pickers';
import { isAdminUser, getLoggedInArea, getAreaDisplayName } from '../../utils/authService';

// ─── shared sub-styles ───────────────────────────────────────────────────────

const selectInput = {
  backgroundColor: 'rgba(255,255,255,0.05)',
  color: 'white',
  borderRadius: '8px',
  fontSize: '0.84rem',
  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.12)' },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#1ABC9C', borderWidth: '1.5px' },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#1ABC9C', borderWidth: '1.5px' },
  '& .MuiSelect-icon': { color: '#1ABC9C' },
};

const dateTimeField = {
  '& .MuiOutlinedInput-root': {
    backgroundColor: 'rgba(255,255,255,0.05)',
    borderRadius: '8px',
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#1ABC9C', borderWidth: '1.5px' },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#1ABC9C', borderWidth: '1.5px' },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.12)' },
  },
  '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.6)', fontSize: '0.84rem' },
  '& .MuiOutlinedInput-input': { color: 'white', fontSize: '0.84rem' },
  '& svg': { color: '#1ABC9C' },
};

const selectMenu = {
  PaperProps: {
    style: {
      maxHeight: 280,
      backgroundColor: '#1a2535',
      border: '1px solid rgba(255,255,255,0.1)',
      borderRadius: '10px',
    },
  },
  keepMounted: true,
};

const menuItemSx = {
  color: 'white',
  fontSize: '0.84rem',
  '&:hover': { backgroundColor: 'rgba(26,188,156,0.1)' },
  '&.Mui-selected': {
    backgroundColor: 'rgba(26,188,156,0.18)',
    '&:hover': { backgroundColor: 'rgba(26,188,156,0.26)' },
  },
};

const checkboxSx = {
  color: 'rgba(255,255,255,0.5)',
  '&.Mui-checked': { color: '#1ABC9C' },
  padding: '4px',
};

const labelSx = { color: 'rgba(255,255,255,0.6)', fontSize: '0.84rem' };
const listItemTextSx = { color: 'white', '& .MuiListItemText-primary': { fontSize: '0.84rem' } };

// ─── priority colours ────────────────────────────────────────────────────────
const priorityColor = { High: '#e74c3c', Moderate: '#f39c12', Low: '#2ecc71', Unassigned: '#64748b', All: '#1ABC9C' };
const closeStatusColor = { All: '#1ABC9C', Open: '#3B82F6', Closed: '#64748b' };

// ─── Section header helper ────────────────────────────────────────────────────
function SectionHeader({ icon, title, hint, accent = '#1ABC9C' }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5, gap: 0.8 }}>
      <Box
        sx={{
          width: 28, height: 28, borderRadius: '7px',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: `${accent}22`, color: accent, flexShrink: 0,
        }}
      >
        {React.cloneElement(icon, { sx: { fontSize: 16 } })}
      </Box>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'rgba(255,255,255,0.9)', fontSize: '0.82rem', letterSpacing: 0.4 }}>
        {title}
      </Typography>
      {hint && (
        <Tooltip title={hint} placement="top">
          <IconButton size="small" sx={{ color: 'rgba(255,255,255,0.3)', p: 0.3, ml: 0.2 }}>
            <HelpOutlineIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
}

// ─── divider ─────────────────────────────────────────────────────────────────
const SectionDivider = () => (
  <Divider sx={{ borderColor: 'rgba(255,255,255,0.07)', my: 2.5 }} />
);

// ─────────────────────────────────────────────────────────────────────────────

const ReportFiltersUI = ({ filterData, handlers }) => {
  const {
    detectionTypes, areas, priorities, closeStatus,
    startTime, endTime,
    DETECTION_TYPES, AREA_OPTIONS, PRIORITY_OPTIONS, CLOSE_STATUS_OPTIONS,
  } = filterData;

  const {
    handleDetectionTypeChange, handleAreaChange,
    handlePriorityChange, handleCloseStatusChange,
    setStartTime, setEndTime, handleApplyFilters,
    getDetectionTypeLabel, getAreaLabel, getPriorityLabel, getCloseStatusLabel,
    isSelected,
  } = handlers;

  return (
    <Box
      sx={{
        minHeight: 'calc(100vh - 130px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: { xs: 2, sm: 3, md: 4 },
        background: 'radial-gradient(ellipse at 50% 50%, rgba(26,188,156,0.06) 0%, transparent 65%)',
      }}
    >
      <Box sx={{ width: '100%', maxWidth: 780 }}>

        {/* ── Page header ─────────────────────────────────────────────── */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
          <Box
            sx={{
              width: 42, height: 42, borderRadius: '11px',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: 'linear-gradient(135deg, #1ABC9C44, #1ABC9C11)',
              border: '1.5px solid rgba(26,188,156,0.35)',
              color: '#1ABC9C', flexShrink: 0,
            }}
          >
            <VideocamIcon sx={{ fontSize: 22 }} />
          </Box>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="h6" sx={{ color: 'white', fontWeight: 700, fontSize: '1rem', letterSpacing: 0.3 }}>
                Video Analytics Report Filters
              </Typography>
              <Box
                component="span"
                sx={{
                  fontSize: '0.68rem', fontWeight: 700, px: 0.8, py: 0.15,
                  borderRadius: '5px', background: 'rgba(26,188,156,0.18)',
                  color: '#1ABC9C', border: '1px solid rgba(26,188,156,0.35)',
                  letterSpacing: 0.5,
                }}
              >
                VA
              </Box>
            </Box>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.75rem' }}>
              Set filters to generate a customised analytics report
            </Typography>
          </Box>
        </Box>

        {/* ── Main filter card ─────────────────────────────────────────── */}
        <Box
          sx={{
            background: 'rgba(22,30,42,0.92)',
            border: '1px solid rgba(255,255,255,0.08)',
            borderRadius: '14px',
            p: { xs: 2.5, sm: 3 },
            backdropFilter: 'blur(12px)',
            boxShadow: '0 8px 40px rgba(0,0,0,0.45)',
          }}
        >

          {/* ── Time Range ────────────────────────────────────────────── */}
          <SectionHeader
            icon={<ScheduleIcon />}
            title="Time Range"
            hint="Select the date/time period for the report"
          />
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
              <DateTimePicker
                label="From"
                value={startTime}
                onChange={(v) => setStartTime(v)}
                renderInput={(params) => (
                  <TextField {...params} fullWidth size="small" sx={dateTimeField} />
                )}
              />
              <DateTimePicker
                label="To"
                value={endTime}
                onChange={(v) => setEndTime(v)}
                renderInput={(params) => (
                  <TextField {...params} fullWidth size="small" sx={dateTimeField} />
                )}
              />
            </Box>
          </LocalizationProvider>

          <SectionDivider />

          {/* ── Detection Filters ──────────────────────────────────────── */}
          <SectionHeader
            icon={<CategoryIcon />}
            title="Detection Filters"
            hint="Narrow results by detection type, area, priority and event status"
          />

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>

            {/* Detection Type */}
            <FormControl fullWidth size="small">
              <InputLabel sx={labelSx}>Detection Type</InputLabel>
              <Select
                multiple
                value={detectionTypes}
                onChange={handleDetectionTypeChange}
                input={<OutlinedInput label="Detection Type" />}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((v) => (
                      <Chip key={v} label={getDetectionTypeLabel(v)} size="small"
                        sx={{ background: 'rgba(26,188,156,0.18)', color: 'white', fontSize: '0.72rem', height: 20 }} />
                    ))}
                  </Box>
                )}
                MenuProps={selectMenu}
                sx={selectInput}
              >
                <MenuItem value="All" sx={menuItemSx}>
                  <Checkbox checked={isSelected(detectionTypes, 'All')} size="small" sx={checkboxSx} />
                  <ListItemText primary="All Types" sx={listItemTextSx} />
                </MenuItem>
                <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)' }} />
                {DETECTION_TYPES.map((t) => (
                  <MenuItem key={t.id} value={t.id} sx={menuItemSx}>
                    <Checkbox checked={isSelected(detectionTypes, t.id)} size="small" sx={checkboxSx} />
                    <ListItemText primary={t.title} sx={listItemTextSx} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Area */}
            {!isAdminUser() && getLoggedInArea() ? (
              <Box
                sx={{
                  display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1,
                  px: 1.5, py: 1,
                  background: 'rgba(26,188,156,0.05)',
                  border: '1px solid rgba(26,188,156,0.25)',
                  borderRadius: '8px',
                }}
              >
                <PlaceIcon sx={{ fontSize: 16, color: '#1ABC9C', flexShrink: 0 }} />
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.78rem' }}>
                  Area:
                </Typography>
                <Chip
                  label={getAreaDisplayName(getLoggedInArea())}
                  size="small"
                  sx={{ background: 'rgba(26,188,156,0.2)', color: '#1ABC9C', fontWeight: 700, fontSize: '0.75rem', border: '1px solid rgba(26,188,156,0.4)' }}
                />
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.7rem', fontStyle: 'italic' }}>
                  (restricted)
                </Typography>
              </Box>
            ) : (
              <FormControl fullWidth size="small">
                <InputLabel sx={labelSx}>Area</InputLabel>
                <Select
                  multiple
                  value={areas}
                  onChange={handleAreaChange}
                  input={<OutlinedInput label="Area" />}
                  renderValue={(selected) => {
                    const allIndividual = AREA_OPTIONS.filter(o => o.value !== 'All Areas').map(o => o.value);
                    const allSelected   = allIndividual.every(a => selected.includes(a));
                    return (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {allSelected ? (
                          <Chip label="All Areas" size="small"
                            sx={{ background: 'rgba(26,188,156,0.18)', color: 'white', fontSize: '0.72rem', height: 20 }} />
                        ) : selected.map((v) => (
                          <Chip key={v} label={getAreaLabel(v)} size="small"
                            sx={{ background: 'rgba(26,188,156,0.18)', color: 'white', fontSize: '0.72rem', height: 20 }} />
                        ))}
                      </Box>
                    );
                  }}
                  MenuProps={selectMenu}
                  sx={selectInput}
                >
                  <MenuItem value="All Areas" sx={menuItemSx}>
                    <Checkbox checked={isSelected(areas, 'All Areas')} size="small" sx={checkboxSx} />
                    <ListItemText primary="All Areas" sx={listItemTextSx} />
                  </MenuItem>
                  <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)' }} />
                  {AREA_OPTIONS.filter(o => o.value !== 'All Areas').map((o) => (
                    <MenuItem key={o.value} value={o.value} sx={menuItemSx}>
                      <Checkbox checked={isSelected(areas, o.value)} size="small" sx={checkboxSx} />
                      <ListItemText primary={o.label} sx={listItemTextSx} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {/* Priority */}
            <FormControl fullWidth size="small">
              <InputLabel sx={labelSx}>Priority</InputLabel>
              <Select
                multiple
                value={priorities}
                onChange={handlePriorityChange}
                input={<OutlinedInput label="Priority" />}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((v) => (
                      <Chip key={v} label={getPriorityLabel(v)} size="small"
                        sx={{ background: `${priorityColor[v] || '#1ABC9C'}33`, color: 'white', fontSize: '0.72rem', height: 20,
                          border: `1px solid ${priorityColor[v] || '#1ABC9C'}55` }} />
                    ))}
                  </Box>
                )}
                MenuProps={selectMenu}
                sx={selectInput}
              >
                <MenuItem value="All" sx={menuItemSx}>
                  <Checkbox checked={isSelected(priorities, 'All')} size="small" sx={checkboxSx} />
                  <ListItemText primary="All Priorities" sx={listItemTextSx} />
                </MenuItem>
                <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)' }} />
                {PRIORITY_OPTIONS.filter(o => o.value !== 'All').map((o) => (
                  <MenuItem key={o.value} value={o.value} sx={menuItemSx}>
                    <Checkbox checked={isSelected(priorities, o.value)} size="small" sx={checkboxSx} />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: priorityColor[o.value] || '#1ABC9C', flexShrink: 0 }} />
                      <ListItemText primary={o.label} sx={listItemTextSx} />
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Event Status */}
            <FormControl fullWidth size="small">
              <InputLabel sx={labelSx}>Event Status</InputLabel>
              <Select
                multiple
                value={closeStatus}
                onChange={handleCloseStatusChange}
                input={<OutlinedInput label="Event Status" />}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((v) => (
                      <Chip key={v} label={getCloseStatusLabel(v)} size="small"
                        sx={{ background: `${closeStatusColor[v] || '#1ABC9C'}33`, color: 'white', fontSize: '0.72rem', height: 20,
                          border: `1px solid ${closeStatusColor[v] || '#1ABC9C'}55` }} />
                    ))}
                  </Box>
                )}
                MenuProps={selectMenu}
                sx={selectInput}
              >
                <MenuItem value="All" sx={menuItemSx}>
                  <Checkbox checked={isSelected(closeStatus, 'All')} size="small" sx={checkboxSx} />
                  <ListItemText primary="All Events" sx={listItemTextSx} />
                </MenuItem>
                <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)' }} />
                {CLOSE_STATUS_OPTIONS.filter(o => o.value !== 'All').map((o) => (
                  <MenuItem key={o.value} value={o.value} sx={menuItemSx}>
                    <Checkbox checked={isSelected(closeStatus, o.value)} size="small" sx={checkboxSx} />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: closeStatusColor[o.value] || '#1ABC9C', flexShrink: 0 }} />
                      <ListItemText primary={o.label} sx={listItemTextSx} />
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

          </Box>

          <SectionDivider />

          {/* ── Apply button ───────────────────────────────────────────── */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              startIcon={<SearchIcon />}
              onClick={handleApplyFilters}
              sx={{
                background: 'linear-gradient(135deg, #1ABC9C, #16a085)',
                color: 'white',
                fontWeight: 700,
                fontSize: '0.85rem',
                letterSpacing: 0.8,
                px: 4,
                py: 1.1,
                borderRadius: '9px',
                boxShadow: '0 4px 18px rgba(26,188,156,0.35)',
                textTransform: 'uppercase',
                transition: 'all 0.22s ease',
                '&:hover': {
                  background: 'linear-gradient(135deg, #1ed9b4, #1ABC9C)',
                  boxShadow: '0 6px 22px rgba(26,188,156,0.45)',
                  transform: 'translateY(-1px)',
                },
                '&:active': { transform: 'translateY(0)' },
              }}
            >
              Apply Filters
            </Button>
          </Box>

        </Box>
      </Box>
    </Box>
  );
};

export default ReportFiltersUI;
